/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { BehaviorSubject, filter, fromEvent, map, merge, Observable, scan, shareReplay, throttleTime } from 'rxjs';

import { findGroupTargetId } from './find-group-target-id';
import { InteractionData, Point } from './interaction-data.type';

// DOM types for event handling
type MouseEvent = globalThis.MouseEvent;
type Element = globalThis.Element;
type TouchEvent = globalThis.TouchEvent;
type KeyboardEvent = globalThis.KeyboardEvent;


const sharedInteraction$: Observable<InteractionData> | null = null;

const defaultState: InteractionData = {
	selected: [],
	keys: [],
	target: null,
	click: { x: 0, y: 0, target: null },
	dblClick: { x: 0, y: 0, target: null },
	pointer: { x: 0, y: 0 },
	drag: { x: 0, y: 0, dx: 0, dy: 0, kx: 0, ky: 0, distance: 0 },
	raw: { x: 0, y: 0 },
	velocity: { x: 0, y: 0 },
	ppi: { x: 0, y: 0 },
	swipe: null,
	isDragging: false,
	isActive: true
};


class InteractionService {
	private static instance: InteractionService;

	private sharedMouse$: Observable<InteractionData> | null = null;
	private stateSubject = new BehaviorSubject<InteractionData>(defaultState);

	/** Singleton accessor */
	static getInstance (): InteractionService {
		if (!InteractionService.instance) {
			InteractionService.instance = new InteractionService();
		}
		return InteractionService.instance;
	}

	private constructor () {
		this.sharedMouse$ = this.buildMouseStream();
		this.sharedMouse$.subscribe((state) => {
			this.stateSubject.next(state);
		});
	}

	/** Stream for reactive hooks */
	get stream (): Observable<InteractionData> {
		return this.stateSubject.asObservable();
	}

	/** Current state snapshot */
	get state (): InteractionData {
		return this.stateSubject.value;
	}

	/** Convenient property getter */
	get isDragging (): boolean {
		return this.state.drag.distance !== 0;
	}

	/** Current Selections */
	get selected (): Array<string> {
		return this.state.selected;
	}

	public isSelected (id: string): boolean {
		return this.state.selected.includes(id);
	}

	// eslint-disable-next-line class-methods-use-this
	private buildMouseStream = (): Observable<InteractionData> => {
		let lastPointer = { x: 0, y: 0, t: performance.now() };
		let dragStart: Point | null = null;

		const keyDown$ = fromEvent<KeyboardEvent>(window, 'keydown').pipe(
			throttleTime(32),
			// filter((e) => ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)),
			map((e) => ({ type: 'keydown', key: e.key?.toLowerCase() ?? '' }))
		);

		const keyUp$ = fromEvent<KeyboardEvent>(window, 'keyup').pipe(map((e) => ({ type: 'keyup', key: e.key?.toLowerCase() ?? '' })));

		const mouseMove$ = fromEvent<MouseEvent>(window, 'mousemove').pipe(
			throttleTime(32),
			map((e) => ({ type: 'move', x: e.clientX, y: e.clientY, time: performance.now(), el: e.target as Element }))
		);

		const mouseDown$ = fromEvent<MouseEvent>(window, 'mousedown').pipe(map((e) => ({ type: 'down', x: e.clientX, y: e.clientY, el: e.target as Element })));

		const mouseUp$ = fromEvent<MouseEvent>(window, 'mouseup').pipe(map((e) => ({ type: 'up', x: e.clientX, y: e.clientY, el: null })));

		const click$ = fromEvent<MouseEvent>(window, 'click').pipe(map((e) => {
			const t = { type: 'click', x: e.clientX, y: e.clientY, button: e.button, el: e.target as Element };
			return t;
		}));

		const dblClick$ = fromEvent<MouseEvent>(window, 'dblclick').pipe(map((e) => ({ type: 'dblclick', x: e.clientX, y: e.clientY, button: e.button, el: e.target as Element })));

		const touchMove$ = fromEvent<TouchEvent>(window, 'touchmove').pipe(
			throttleTime(32),
			map((e) => ({
				type: 'move',
				x: e.touches[0]?.clientX || 0,
				y: e.touches[0]?.clientY || 0,
				el: e.target as Element,
				time: performance.now()
			}))
		);

		const touchStart$ = fromEvent<TouchEvent>(window, 'touchstart').pipe(map((e) => ({
			type: 'down',
			x: e.touches[0]?.clientX || 0,
			y: e.touches[0]?.clientY || 0,
			el: e.target as Element
		})));

		const touchEnd$ = fromEvent<TouchEvent>(window, 'touchend').pipe(map((e) => ({
			type: 'up',
			x: e.changedTouches[0]?.clientX || 0,
			y: e.changedTouches[0]?.clientY || 0,
			el: null
		})));

		return merge(keyDown$, keyUp$, mouseMove$, mouseDown$, mouseUp$, click$, dblClick$, touchMove$, touchStart$, touchEnd$).pipe(

			scan((state: InteractionData, event: any): InteractionData => {
				const raw: Point = { x: event.x ?? state.raw.x, y: event.y ?? state.raw.y };
				const now = event.time || performance.now();
				const dt = (now - lastPointer.t) / 1000;
				const dx = raw.x - lastPointer.x;
				const dy = raw.y - lastPointer.y;
				const vx = dt > 0 ? dx / dt : 0;
				const vy = dt > 0 ? dy / dt : 0;
				const id = findGroupTargetId(event.el);

				const newState: InteractionData = {
					...state,
					raw,
					target: event.el ?? null,
					velocity: { x: vx, y: vy },
					pointer: { x: raw.x, y: raw.y }
				};

				lastPointer = { x: raw.x, y: raw.y, t: now };

				// handles keys
				if (event.key) {
					if (event.type === 'keydown' && !newState.keys.includes(event.key)) {
						newState.keys = [...state.keys, event.key];
					} else if (newState.keys.includes(event.key)) {
						newState.keys = state.keys.filter((k) => k !== event.key);
					}
				}

				// handle activate id tracking (selections)
				if (event.type === 'down' || event.type === 'click') {
					const isMultiSelect = newState.keys.includes('control') || newState.keys.includes('meta');
					if (id) {
						if (isMultiSelect) {
							// Add to selection if not already selected
							if (!newState.selected.includes(id)) {
								newState.selected = [...state.selected, id];
							}
						} else {
							newState.selected = [id];
						}
					} else {
						newState.selected = [];
					}
				}

				if (dragStart) {
					const dragDx = raw.x - dragStart.x;
					const dragDy = raw.y - dragStart.y;

					// newState.isDragging = true;
					newState.drag = {
						x: dragStart.x,
						y: dragStart.y,
						dx: dragDx,
						dy: dragDy,
						kx: state.drag.kx,
						ky: state.drag.ky,
						distance: Math.hypot(dragDx, dragDy)
					};

					if (event.type === 'keydown') {
						switch (event.key) {
							case 'ArrowUp':
								newState.drag.ky++;
								break;
							case 'ArrowDown':
								newState.drag.ky--;
								break;
							case 'ArrowLeft':
								newState.drag.kx++;
								break;
							case 'ArrowRight':
								newState.drag.kx--;
								break;
							default:
								break;
						}
					}

					if (event.type === 'up' && newState.drag.distance > 30) {
						const angle = Math.atan2(dragDy, dragDx);
						newState.swipe = angle;
					}
				}

				if (event.type === 'down') {
					dragStart = { ...raw };
					newState.swipe = null;
					newState.isDragging = true;
					newState.drag = {
						...state.drag,
						...raw
					};
				}

				if (event.type === 'up') {
					dragStart = null;
					newState.isDragging = false;
					newState.drag = {
						x: 0,
						y: 0,
						dx: 0,
						dy: 0,
						kx: 0,
						ky: 0,
						distance: 0
					};
				}

				if (event.type === 'click') {
					newState.click = { ...raw, target: event.el ?? null };
					// newState.swipe = null;
				}

				if (event.type === 'dblclick') {
					newState.dblClick = { ...raw, target: event.el ?? null };
				}

				return newState;
			}, defaultState),
			shareReplay(1)
		);
	};
}

export { InteractionService };
export const interactionService = InteractionService.getInstance();
